'use client';

import React, { FC, useState } from 'react';
import Script from 'next/script';
import {
  TypeLink_external,
  TypeLink_internal,
  TypePage,
} from '@/core/generated-types';

import useFeature from '@/hooks/useFeature';
import { useYotpoScript } from '@/hooks/useYotpoScript';
import useLocale from '@/hooks/useLocale';

import {
  DynamicComponent,
  ExternalLink,
  HeaderText,
  InternalLink,
  NotFound,
} from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { Seo } from '@/components/ui/Seo';
import { buildCanonicalUrl } from '@/core/contentfulConfig';
import { useTrackPlayKitPLPProducts } from '@/hooks/useTrackPlayKitPLPProducts';
import { CarouselContextProvider } from '@/context/CarouselContext';
import { FeatureFlagValues } from '@/core/featureFlags';

export const PageLayout: FC<TypePage> = (page) => {
  const locale = useLocale();
  const [carouselImagePosition, setCarouselImagePosition] = useState(null);

  const {
    fields: { title, components, longDescription, featureFlagName },
  } = page;
  const metadataFields = page.fields?.metadata?.fields;

  const isFlagEnabled = useFeature(featureFlagName as keyof FeatureFlagValues);
  const isPageEnabled = isFlagEnabled ?? true;

  useYotpoScript();
  useTrackPlayKitPLPProducts();

  const isTermsPage = page.fields?.components?.some(
    (c) => c?.fields?.anchorId === 'terms-block',
  );

  if (!isPageEnabled) {
    return <NotFound />;
  }

  return (
    <CarouselContextProvider
      value={{ carouselImagePosition, setCarouselImagePosition }}
    >
      <Seo
        title={metadataFields?.pageTitle}
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow}
        noindex={metadataFields?.noIndex}
        canonical={buildCanonicalUrl(page)}
        openGraph={{
          url: buildCanonicalUrl(page),
        }}
      />
      <div className="overflow-hidden display-unset">
        {title && (
          <HeaderText
            size={HeaderSize.H1}
            classes="container mx-auto mt-8 text-center font-bold"
            text={title}
            hoverEffect
          />
        )}
        {longDescription && (
          <p className="container mx-auto">{longDescription}</p>
        )}
        <div className="display-unset">
          {components &&
            components.map((component, i) =>
              component?.sys?.contentType?.sys?.id === 'link_internal' ? (
                <InternalLink
                  key={`${component.sys.id}-${i}`}
                  link={component as TypeLink_internal}
                />
              ) : component?.sys?.contentType?.sys?.id === 'link_external' ? (
                <ExternalLink
                  key={`${component.sys.id}-${i}`}
                  link={component as TypeLink_external}
                />
              ) : (
                <DynamicComponent
                  key={`${component.sys.id}-${i}`}
                  component={component}
                />
              ),
            )}
          {locale === 'en-US' && isTermsPage && (
            <>
              <div
                id="accessibility-statement"
                className="relative -top-[96px]"
              />
              <div
                id="a40AccessibilityStatement"
                className="rte container mx-auto w-full px-4 pb-10 lg:pb-20"
              ></div>
              <Script
                type="text/javascript"
                src="https://lovevery.usablenet.com/pt/accessibilitystatement.js?l=1"
              />
            </>
          )}
        </div>
      </div>
    </CarouselContextProvider>
  );
};
